import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  RefreshCw,
  ChevronLeft,
  Calculator,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Eraser,
  Plus,
  X,
  FlaskConical,
  TrendingUp,
  MessageSquare,
  Loader2
} from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-3.5 py-2 text-sm font-medium bg-white text-gray-700 ' +
  'border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes np-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
@keyframes np-pop { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.np-toast-in { animation: np-toast-in .2s ease-out; }
.np-pop { animation: np-pop .2s ease-out; }
@media (prefers-reduced-motion: reduce) { .np-toast-in, .np-pop { animation: none; } }
`;

const OPERATORS = [
  { label: '+', value: '+' },
  { label: '−', value: '-' },
  { label: '×', value: '*' },
  { label: '÷', value: '/' },
  { label: '(', value: '(' },
  { label: ')', value: ')' }
];

/* ------------------------------------------------------------------ */
/*  Analyse de formule (aide visuelle côté client uniquement)          */
/* ------------------------------------------------------------------ */

const TOKEN_RE = /([A-Za-zÀ-ÿ_][\wÀ-ÿ]*)|(\d+(?:[.,]\d+)?)|([+\-*/%<>=!&|?:,]+)|([()])|(\s+)|([\s\S])/g;

const analyse = (formula, knownVars) => {
  const tokens = [];
  const unknown = new Set();
  let depth = 0;
  let balanced = true;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(formula)) !== null) {
    const [text, ident, num, op, paren, space] = m;
    if (ident) {
      const next = formula.slice(m.index + text.length).trimStart()[0];
      const prev = formula[m.index - 1];
      const isFunctionLike = next === '(' || prev === '.' || ident === 'Math';
      if (knownVars.has(ident)) tokens.push({ text, kind: 'var' });
      else if (isFunctionLike) tokens.push({ text, kind: 'fn' });
      else {
        unknown.add(ident);
        tokens.push({ text, kind: 'unknown' });
      }
    } else if (num) tokens.push({ text, kind: 'num' });
    else if (op) tokens.push({ text, kind: 'op' });
    else if (paren) {
      if (paren === '(') depth += 1;
      else {
        depth -= 1;
        if (depth < 0) balanced = false;
      }
      tokens.push({ text, kind: 'paren' });
    } else if (space) tokens.push({ text, kind: 'space' });
    else tokens.push({ text, kind: 'other' });
  }
  if (depth !== 0) balanced = false;
  return { tokens, unknown: [...unknown], balanced };
};

const tokenClass = {
  var: 'bg-blue-100 text-blue-800 rounded-sm',
  fn: 'text-purple-700',
  num: 'text-amber-700',
  op: 'text-gray-500',
  paren: 'text-purple-600 font-semibold',
  unknown: 'text-red-600 underline decoration-wavy decoration-red-400',
  space: '',
  other: 'text-gray-800'
};

/* ------------------------------------------------------------------ */
/*  Éditeur de formule                                                 */
/* ------------------------------------------------------------------ */

const FormulaEditor = ({ icon: Icon, title, subtitle, value, onChange, placeholder, indicateurs, knownVars }) => {
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const { tokens, unknown, balanced } = useMemo(() => analyse(value, knownVars), [value, knownVars]);

  const insert = (text) => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const padBefore = before && !/\s$/.test(before) ? ' ' : '';
    const padAfter = after && !/^\s/.test(after) ? ' ' : '';
    const next = before + padBefore + text + padAfter + after;
    onChange(next);
    const caret = (before + padBefore + text + padAfter).length;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(caret, caret);
    });
  };

  const syncScroll = () => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const isEmpty = value.trim() === '';
  const textBox = 'font-mono text-sm leading-6 p-4 whitespace-pre-wrap break-words';

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* En-tête */}
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
          {Icon ? <Icon className="h-5 w-5" /> : <Calculator className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      {/* Palette : indicateurs + opérateurs */}
      <div className="space-y-3 border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
        <div>
          <div className="mb-2 text-xs font-medium text-gray-500">Cliquez sur un indicateur pour l'insérer</div>
          {indicateurs.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun indicateur associé à ce type de personnel.</p>
          ) : (
            <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto pr-1">
              {indicateurs.map((ind) => {
                const variable = ind.nom_variable || ind.Libelle_indicateur;
                return (
                  <button
                    key={ind.idindicateur}
                    type="button"
                    onClick={() => insert(variable)}
                    title={`Insérer ${variable}`}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-sm text-blue-800 transition-all hover:border-blue-400 hover:bg-blue-50 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                  >
                    <Plus className="h-3.5 w-3.5 text-blue-400 transition-colors group-hover:text-blue-600" />
                    {ind.Libelle_indicateur}
                    <span className="font-mono text-xs text-gray-400">
                      {ind.nom_variable || 'sans variable'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-medium text-gray-500">Opérateurs</span>
          {OPERATORS.map((op) => (
            <button
              key={op.value}
              type="button"
              onClick={() => insert(op.value)}
              aria-label={`Insérer ${op.label}`}
              className="h-9 w-9 rounded-lg border border-gray-200 bg-white font-mono text-base text-gray-700 transition-all hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/30"
            >
              {op.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange('')}
            disabled={isEmpty}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium text-gray-500 transition-colors hover:bg-white hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Eraser className="h-3.5 w-3.5" />
            Effacer
          </button>
        </div>
      </div>

      {/* Zone d'édition avec coloration */}
      <div className="px-5 py-5 sm:px-6">
        <div
          className={`relative rounded-xl border bg-white transition-all ${
            focused ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-gray-200'
          }`}
        >
          <div
            ref={overlayRef}
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 overflow-hidden ${textBox}`}
          >
            {tokens.map((t, i) => (
              <span key={i} className={tokenClass[t.kind]}>
                {t.text}
              </span>
            ))}
            {'\n'}
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={syncScroll}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            spellCheck={false}
            rows={6}
            placeholder={placeholder}
            className={`relative block min-h-[9rem] w-full resize-y rounded-xl bg-transparent text-transparent caret-gray-900 placeholder-gray-400 focus:outline-none ${textBox}`}
          />
        </div>

        {/* Barre d'état */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {isEmpty ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-600">
              Formule vide
            </span>
          ) : (
            <>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
                  balanced ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {balanced ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                {balanced ? 'Parenthèses équilibrées' : 'Parenthèses à vérifier'}
              </span>
              {unknown.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-800">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Variable inconnue : {unknown.join(', ')}
                </span>
              )}
            </>
          )}
          <span className="ml-auto text-gray-400">{value.length} caractères</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Résultat de test                                                   */
/* ------------------------------------------------------------------ */

const ResultTile = ({ label, value, tone }) => (
  <div className={`np-pop rounded-xl border px-4 py-3 ${tone}`}>
    <div className="text-xs font-medium opacity-80">{label}</div>
    <div className="mt-0.5 text-3xl font-semibold tracking-tight">{String(value)}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const NotePonderee = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();

  const [typePersonnel, setTypePersonnel] = useState(null);
  const [indicateurs, setIndicateurs] = useState([]);
  const [formuleQuanti, setFormuleQuanti] = useState('');
  const [formuleQuali, setFormuleQuali] = useState('');
  const [saved, setSaved] = useState({ quanti: '', quali: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testValues, setTestValues] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [toasts, setToasts] = useState([]);

  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  useEffect(() => {
    loadData();
  }, [typeId, filialeId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [typesRes, indicateursRes, formuleRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get(`/api/indicateur-association/${typeId}`).catch(() => ({ data: [] })),
        http.get(`/api/note-pondere/${typeId}`).catch(() => ({ data: null }))
      ]);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const type = allTypes.find((t) => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(type);

      const allIndicateurs = Array.isArray(indicateursRes.data) ? indicateursRes.data : [];
      setIndicateurs(allIndicateurs);

      if (formuleRes.data) {
        const q = formuleRes.data.formule_quanti || '';
        const l = formuleRes.data.formule_quali || '';
        setFormuleQuanti(q);
        setFormuleQuali(l);
        setSaved({ quanti: q, quali: l });
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);

      await http.post('/api/note-pondere', {
        idtype_personnel: parseInt(typeId),
        filiale_id: filialeId || 1,
        formule_quanti: formuleQuanti,
        formule_quali: formuleQuali
      });

      setSaved({ quanti: formuleQuanti, quali: formuleQuali });
      notify('success', 'Formules sauvegardées avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      notify('error', 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      const response = await http.post('/api/note-pondere/test', {
        formule_quanti: formuleQuanti,
        formule_quali: formuleQuali,
        valeurs: testValues
      });

      setTestResult(response.data);
    } catch (error) {
      console.error('Erreur test:', error);
      setTestResult({
        error: error.response?.data?.error || 'Erreur lors du test de calcul'
      });
    } finally {
      setTesting(false);
    }
  };

  // Raccourci Ctrl/Cmd + S pour sauvegarder
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const knownVars = useMemo(
    () => new Set(indicateurs.map((ind) => ind.nom_variable || ind.Libelle_indicateur).filter(Boolean)),
    [indicateurs]
  );

  const dirty = formuleQuanti !== saved.quanti || formuleQuali !== saved.quali;

  /* ---------- Rendu ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{keyframes}</style>

      <BlueHeader
        title={`Note Pondérée : ${typePersonnel?.libelle || 'Type Personnel'}`}
        subtitle="Définir les formules de calcul de la note pondérée"
        actions={[
          {
            label: 'Retour',
            icon: ChevronLeft,
            onClick: () => navigate(`/types-personnel/${typePersonnel?.idactivite || 1}`),
            primary: false
          },
          {
            label: 'Sauvegarder',
            icon: Save,
            onClick: handleSave,
            primary: true,
            disabled: saving
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          }
        ]}
      />

      <div className="px-4 py-6 sm:px-6 sm:py-8">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {[0, 1].map((i) => (
                <div key={i} className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                  <div className="h-5 w-64 animate-pulse rounded bg-gray-100" />
                  <div className="h-10 animate-pulse rounded-xl bg-gray-100" />
                  <div className="h-36 animate-pulse rounded-xl bg-gray-100" />
                </div>
              ))}
            </div>
            <div className="h-96 animate-pulse rounded-2xl border border-gray-100 bg-white shadow-sm" />
          </div>
        ) : (
          <>
            {/* Indicateur de modifications non enregistrées */}
            <div className="mb-4 flex min-h-[2rem] items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Construisez vos formules avec les indicateurs, puis testez-les avec des valeurs fictives.
              </p>
              {dirty ? (
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Modifications non enregistrées
                  </span>
                  <button onClick={handleSave} disabled={saving} className={primaryBtn}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  Tout est enregistré
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
              {/* Éditeurs */}
              <div className="space-y-6 lg:col-span-2">
                <FormulaEditor
                  icon={TrendingUp}
                  title="Formule de calcul note pondérée quantitative"
                  subtitle="Combinez vos indicateurs chiffrés avec des coefficients."
                  value={formuleQuanti}
                  onChange={setFormuleQuanti}
                  placeholder="Ex: (Taux_de_presence * 0.6) + (TMS * 0.4)"
                  indicateurs={indicateurs}
                  knownVars={knownVars}
                />
                <FormulaEditor
                  icon={MessageSquare}
                  title="Formule de calcul note pondérée qualitative"
                  subtitle="Pondérez les indicateurs d'appréciation qualitative."
                  value={formuleQuali}
                  onChange={setFormuleQuali}
                  placeholder="Ex: (Qualite_service * 0.7) + (Satisfaction_client * 0.3)"
                  indicateurs={indicateurs}
                  knownVars={knownVars}
                />
              </div>

              {/* Panneau de test */}
              <aside className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6">
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <FlaskConical className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Test de calcul de la note</h3>
                      <p className="text-sm text-gray-500">Saisissez des valeurs et lancez le calcul.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 px-5 py-5">
                  {/* Résultat */}
                  {testResult && (
                    <div aria-live="polite">
                      {/* Afficher l'erreur seulement si les deux formules ont échoué */}
                      {testResult.error && testResult.note_quanti === undefined && testResult.note_quali === undefined && (
                        <div className="np-pop flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                          <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
                          <span>{testResult.error}</span>
                        </div>
                      )}
                      
                      {/* Afficher les résultats disponibles */}
                      {testResult.note_quanti !== undefined || testResult.note_quali !== undefined ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                          {testResult.note_quanti !== undefined && (
                            <ResultTile
                              label="Note quantitative"
                              value={testResult.note_quanti}
                              tone="border-green-200 bg-green-50 text-green-800"
                            />
                          )}
                          {testResult.note_quali !== undefined && (
                            <ResultTile
                              label="Note qualitative"
                              value={testResult.note_quali}
                              tone="border-purple-200 bg-purple-50 text-purple-800"
                            />
                          )}
                        </div>
                      ) : null}
                      
                      {/* Afficher les erreurs spécifiques comme avertissements */}
                      {testResult.error_quanti && (
                        <div className="mt-2 text-xs text-amber-600">
                          {testResult.error_quanti}
                        </div>
                      )}
                      {testResult.error_quali && (
                        <div className="mt-2 text-xs text-amber-600">
                          {testResult.error_quali}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Valeurs de test */}
                  {indicateurs.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-6 text-center text-sm text-gray-500">
                      Aucun indicateur associé à ce type de personnel : il n'y a rien à tester pour l'instant.
                    </p>
                  ) : (
                    <div className="max-h-[22rem] space-y-3 overflow-y-auto pr-1">
                      {indicateurs.map((ind) => (
                        <div key={ind.idindicateur}>
                          <label className="mb-1 flex items-baseline justify-between gap-2 text-sm font-medium text-gray-700">
                            <span className="truncate">{ind.Libelle_indicateur}</span>
                            <span className="shrink-0 font-mono text-xs font-normal text-gray-400">
                              {ind.nom_variable || 'sans variable'}
                            </span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={testValues[ind.nom_variable] || ''}
                            onChange={(e) => {
                              // Gérer à la fois le point et la virgule
                              let value = e.target.value.replace(',', '.');
                              setTestValues({
                                ...testValues,
                                [ind.nom_variable]: parseFloat(value) || 0
                              });
                            }}
                            className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm placeholder-gray-400 transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                            placeholder="0.00"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button onClick={handleTest} disabled={testing} className={`${primaryBtn} flex-1`}>
                      {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      {testing ? 'Calcul en cours...' : 'Tester'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTestValues({});
                        setTestResult(null);
                      }}
                      className={secondaryBtn}
                      title="Vider les valeurs et le résultat"
                    >
                      <Eraser className="h-4 w-4" />
                      Vider
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>

      {/* Notifications */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const isSuccess = t.type === 'success';
          const Icon = isSuccess ? CheckCircle : AlertCircle;
          return (
            <div
              key={t.id}
              className={`np-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg ${
                isSuccess ? 'border-green-200' : 'border-red-200'
              }`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${isSuccess ? 'text-green-600' : 'text-red-600'}`} />
              <p className="flex-1 text-sm text-gray-800">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotePonderee;